import type { ReactNode } from "react";
import { Link } from "react-router";
import type { QueueItem, SpaceEvent } from "@repo/shared-types";
import { partLabel } from "@/lib/event-form";
import { divisionLabel, levelLabel } from "@/lib/results";
import { memberAsPerson, teamDivisionLabel, teamNames } from "@/lib/teams";
import { Avatar, AvatarStack } from "@/components/avatar";
import { scoreText } from "@/components/leaderboard";
import { Badge } from "@/components/ui/badge";

/** What the queue's items and the hidden list's have in common. */
type Reviewed = Pick<QueueItem, "partId" | "athlete" | "level" | "division" | "rxPlus" | "scores" | "team">;

/**
 * One result a verifier or a moderator acts on — waiting in an event's queue,
 * or among its hidden results — laid out the same way every time (decided
 * with Tiago 12 October), so the buttons are where the thumb left them on the
 * last one: the part and its board, with a badge saying what it is; who and
 * the score; when, in muted words; then what to do, in a row of its own. A
 * team's is the same, its people in place of one athlete.
 */
export function ReviewItem({
  event,
  item,
  photo = false,
  badge,
  teamLink,
  when,
  children,
  actions,
}: {
  event: SpaceEvent;
  item: Reviewed;
  /** A workout's photo, in place of a part's result: no part, no board, no score. */
  photo?: boolean;
  badge?: ReactNode;
  /** Where the team's name leads — its own page — when the viewer may open it. */
  teamLink?: string;
  /** The muted line under who: when it changed, or was hidden, and by whom. */
  when: ReactNode;
  /** Anything more to know before acting, under that. */
  children?: ReactNode;
  actions: ReactNode;
}) {
  const index = event.parts.findIndex((part) => part.id === item.partId);
  const part = event.parts[index];
  const team = item.team;
  const [first, ...tiebreaks] = item.scores;
  const board = [
    item.level ? levelLabel(item.level, item.rxPlus) : "",
    item.division ? (team ? teamDivisionLabel(item.division) : divisionLabel(item.division)) : "",
  ].filter(Boolean);
  const name = team ? teamNames(team) || "A team" : (item.athlete.name ?? `@${item.athlete.username}`);

  return (
    <li className="space-y-2 px-4 py-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 text-muted-foreground">
          <span className="font-medium text-foreground">
            {photo ? "A workout's photo" : part ? `${partLabel(index)}. ${part.title}` : "A part since deleted"}
          </span>
          {board.map((label, position) => (
            <span key={position}> · {label}</span>
          ))}
        </p>
        {badge}
      </div>
      {/* A long score — rounds and reps — goes under who, rather than squeezing a name apart. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="flex min-w-0 flex-auto items-center gap-3">
          {team ? <AvatarStack people={team.members.map(memberAsPerson)} /> : <Avatar person={item.athlete} size="md" />}
          <p className="min-w-0 flex-1 [overflow-wrap:anywhere]">
            {team && teamLink ? (
              <Link to={teamLink} className="font-medium hover:underline">
                {name}
              </Link>
            ) : (
              <span className="font-medium">{name}</span>
            )}
            {!team && item.athlete.name && <span className="text-muted-foreground"> @{item.athlete.username}</span>}
          </p>
        </div>
        {first && (
          <span className="ml-auto shrink-0 text-right">
            <span className={scoreText}>{first.display}</span>
            {tiebreaks
              .filter((tiebreak) => tiebreak.display)
              .map((tiebreak, position) => (
                <span key={position} className="block text-xs text-muted-foreground">
                  then {tiebreak.display}
                </span>
              ))}
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{when}</p>
      {children}
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </li>
  );
}

/**
 * A word in warning tones in an item's top corner — why it waits — and the
 * same word before its one explanation at the top of the page.
 */
export function ItemBadge({ children }: { children: ReactNode }) {
  return <Badge className="bg-warning-muted text-warning">{children}</Badge>;
}
